<template>
  <div class="g6-demo">
    
    <!-- 白板词条列表悬浮框 -->
    <transition name="entry-list-fade">
      <div v-if="showEntryList && entryList.length > 0" class="entry-list-panel">
        <div class="entry-list-header">
          <span>白板词条</span>
          <i class="el-icon-close" @click="showEntryList = false"></i>
        </div>
        <div class="entry-list-content">
          <div 
            v-for="(entry, index) in entryList" 
            :key="`entry-${entry.id}-${index}`"
            class="entry-item"
            @click="handleEntryClick(entry)">
            {{ entry.name }}
          </div>
        </div>
      </div>
    </transition>
    
    <div id="container"></div>
    
    <!-- 流程进度触发图标 -->
    <div v-if="isFlowActive" class="flow-trigger-icon" @click="showFlowDialog = true">
      <i class="el-icon-s-operation"></i>
      <div class="icon-pulse"></div>
    </div>

    <!-- 流程进度弹窗 -->
    <ProcessFlowDialog :visible.sync="showFlowDialog" />

    <div class="right-con" v-loading="loading">
      <div class="top">
        <h3 class="title">{{ label }}</h3>
        <p class="desc" v-html="desc"></p>
        <!-- 书籍跳转链接 -->
        <div v-if="bookInfo && bookInfo.book_name" class="book-link-container">
          <a href="javascript:void(0)" @click="handleBookLinkClick" class="book-link">
            <i class="el-icon-reading"></i>
            <span>《{{ bookInfo.book_name }}》</span>
            <i class="el-icon-right"></i>
          </a>
        </div>
      </div>
      
      <!-- 相关词条容器 -->
      <div class="related-keywords-container">
        <div class="related-keywords-header">
          <div class="related-keywords-title">相关词条</div>
          <i 
            class="el-icon-circle-plus-outline add-keyword-btn" 
            :class="{ 'disabled': correlationKeywordsList.length === 0 }"
            @click="handleAddKeywordClick"
          ></i>
        </div>
        <div class="related-keywords-list">
          <div 
            v-if="relatedKeywords.length > 0"
            v-for="(keyword, index) in relatedKeywords" 
            :key="index"
            class="related-keyword-item"
            @click="handleRelatedKeywordClick(keyword)"
          >
            {{ keyword }}
          </div>
          <div v-else class="empty-placeholder">
            暂无相关词条
          </div>
        </div>
      </div>
      
      <!-- 相关词条选择弹窗 -->
      <el-dialog
        :title="inputValue || '选择词条'"
        :visible.sync="showKeywordDialog"
        width="400px"
        custom-class="keyword-selection-dialog"
      >
        <div class="keyword-selection-content">
          <div class="selection-section">
            <div class="section-label">选择关键词</div>
            <div class="keywords-grid">
              <div 
                v-if="correlationKeywordsList.length > 0"
                v-for="(item, index) in correlationKeywordsList" 
                :key="index"
                class="keyword-selection-item"
                :class="{ 'selected': selectedKeyword === item }"
                @click="selectKeyword(item)"
              >
                {{ item }}
              </div>
              <div v-else class="empty-keywords">
                暂无可选词条
              </div>
            </div>
          </div>
          
          <div class="selection-section">
            <div class="section-label">选择关系类型</div>
            <div class="arrows-grid">
              <div 
                v-for="(arrow, index) in arrowImages" 
                :key="index"
                class="arrow-item"
                :class="{ 'selected': selectedArrowIndex === index + 1 }"
                @click="selectArrow(index + 1)"
              >
                <img :src="arrow.url" :alt="arrow.name" />
              </div>
            </div>
          </div>
          
          <div class="action-section">
            <el-button 
              type="primary" 
              @click="saveKeywordRelation" 
              :loading="isSaving"
              :disabled="!selectedKeyword || !selectedArrowIndex"
              size="small"
            >
              {{ isSaving ? '保存中...' : '保存' }}
            </el-button>
            <el-button @click="resetSelection" size="small">重置</el-button>
          </div>
        </div>
      </el-dialog>
      
      <div class="tag-list">
        <div class="tag-list-header">
          <h3 class="title">相关标签</h3>
          <div class="add-tag-input">
            <el-autocomplete
              v-model="tagValue"
              :fetch-suggestions="queryTagSuggestions"
              :trigger-on-focus="false"
              placeholder="添加标签"
              @select="handleTagSelect"
              @keyup.enter.native="handleAddTagEnter"
              size="small"
              clearable
              :debounce="300"
            >
              <template slot-scope="{ item }">
                <div class="tag-suggestion-item">
                  <i class="el-icon-price-tag"></i>
                  <span class="tag-suggestion-text">{{ item.value }}</span>
                </div>
              </template>
            </el-autocomplete>
          </div>
        </div>
        <div class="content-wrapper" :class="{ 'has-ai-tags': aiTags.length > 0 }">
          <!-- 相关词条区域 -->
          <div class="related-tags-section">
            <div class="scrollable-content">
              <el-checkbox-group v-model="checkList" @change="handleCheckboxChange">
                <el-checkbox v-for="(item, idx) in tagList" :key="idx" :label="item"></el-checkbox>
              </el-checkbox-group>
            </div>
          </div>
          
          <!-- 分割线和AI推荐区域 -->
          <div v-if="aiTags.length > 0" class="ai-separator-section">
            <div class="separator">
              <div class="separator-line"></div>
              <span class="separator-text">AI推荐标签</span>
              <div class="separator-line"></div>
            </div>
            
            <div class="ai-tags-section">
              <div class="scrollable-content">
                <el-checkbox-group v-model="selectedAiTags">
                  <el-checkbox v-for="(tag, index) in aiTags" :key="`ai-${index}`" :label="tag"></el-checkbox>
                </el-checkbox-group>
              </div>
              <div class="ai-actions">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="addSelectedAiTags" 
                  :disabled="!selectedAiTags.length"
                  class="ai-add-btn">
                  添加选中标签
                </el-button>
                <el-button 
                  type="text" 
                  size="small" 
                  @click="clearAiTags"
                  class="ai-clear-btn">
                  清除推荐
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- AI获取按钮 - 只在没有AI推荐时显示 -->
      <div v-if="aiTags.length === 0" class="action-section">
        <el-button 
          type="warning" 
          @click="getAiTags" 
          :loading="isGettingAiTags"
          class="ai-btn">
          {{ isGettingAiTags ? '获取中...' : 'AI推荐' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/http/request';
import { ExtensionCategory, Graph, register } from '@antv/g6';
import BiLayout from './BiLayout';
import { getBiData, getPlugins, toFriendList, getLabelList } from './g6util';
import { mapState } from 'vuex';
import ProcessFlowDialog from '@/components/process-flow/ProcessFlowDialog.vue';

let graph = null;

export default {
  components: {
    ProcessFlowDialog
  },
  data() {
    return {
      loading: true,
      graphData: null,
      inputValue: '',
      label: '',
      desc: '',
      tagList: [],
      tagValue: '',
      currentKeywordsId: null,
      memberKeywordsId: null,
      tagListWithIds: [],
      source: 1, //  公共词条 1， 个人词条 2
      isSubmitting: false,
      checkList: [],
      aiTags: [],
      selectedAiTags: [],
      isGettingAiTags: false,
      relatedKeywords: [], // 相关词条列表
      searchSuggestions: [], // 搜索建议列表
      tagSuggestions: [], // 标签建议列表
      showEntryList: false, // 是否显示词条列表
      showFlowDialog: false, // 流程图弹窗显示状态
      showKeywordDialog: false, // 相关词条选择弹窗显示状态
      correlationKeywordsList: [], // 从接口获取的相关词条列表
      selectedKeyword: '', // 选中的关键词
      selectedArrowIndex: null, // 选中的箭头索引（1-4）
      isSaving: false, // 是否正在保存
      keywordsInfo: null, // 保存keywords_info（包含书籍信息）
      arrowImages: [
        { url: 'https://oss.bookor.com.cn/static/img/mini_programs/images/20220309/arrow-1.png', name: 'arrow-1' },
        { url: 'https://oss.bookor.com.cn/static/img/mini_programs/images/20220309/arrow-2.png', name: 'arrow-2' },
        { url: 'https://oss.bookor.com.cn/static/img/mini_programs/images/20220309/arrow-3.png', name: 'arrow-3' },
        { url: 'https://oss.bookor.com.cn/static/img/mini_programs/images/20220309/arrow-4.png', name: 'arrow-4' }
      ]
    }
  },
  computed: {
    ...mapState('processFlow', ['isFlowActive']),
    // 对entryList进行去重处理
    entryList() {
      const rawList = this.$store.state.processFlow.entryList || []
      // 使用Map进行去重，以id为key
      const uniqueMap = new Map()
      rawList.forEach(entry => {
        if (entry && entry.id && !uniqueMap.has(entry.id)) {
          uniqueMap.set(entry.id, entry)
        }
      })
      return Array.from(uniqueMap.values())
    },
    // 获取书籍信息
    bookInfo() {
      if (this.keywordsInfo && 
          this.keywordsInfo.description_all && 
          Array.isArray(this.keywordsInfo.description_all) &&
          this.keywordsInfo.description_all.length > 0 &&
          this.keywordsInfo.description_all[0].book) {
        return this.keywordsInfo.description_all[0].book;
      }
      return null;
    }
  },
  watch: {
    // 监听路由变化，如果是从白板跳转过来的，处理相应逻辑
    '$route.query': {
      handler(newQuery) {
        if (newQuery.fromDraft) {
          if (newQuery.source === 'flow') {
            // 从流程跳转过来，显示词条列表
            this.showEntryList = true
            // 自动搜索第一个词条
            this.$nextTick(() => {
              if (this.entryList && this.entryList.length > 0) {
                this.inputValue = this.entryList[0].name
                // 延迟执行搜索，确保页面已完全加载
                setTimeout(() => {
                  this.handlePressEnter()
                }, 500)
              }
            })
          } else if (newQuery.source === 'direct' && newQuery.keyword) {
            // 从白板直接跳转过来，显示词条列表并使用传递的关键词进行搜索
            this.showEntryList = true
            this.inputValue = newQuery.keyword
            this.$nextTick(() => {
              setTimeout(() => {
                this.handlePressEnter()
              }, 500)
            })
          }
        }
      },
      immediate: true
    },
    // 监听弹窗关闭，重置选择
    showKeywordDialog(val) {
      if (!val) {
        this.resetSelection();
      }
    }
  },
  methods: {
    // 格式化描述文本，在小标题前添加换行
    formatDescription(description) {
      if (!description) return '';
      
      // 定义常见的小标题关键词
      const subtitlePatterns = [
        '属性：', '事件：', '时间：', '地点：', '人物：', '背景：', 
        '原因：', '结果：', '影响：', '意义：', '特点：', '作用：',
        '定义：', '分类：', '组成：', '功能：', '方法：', '步骤：'
      ];
      
      let formattedDesc = description;
      
      // 为每个小标题添加换行标签
      subtitlePatterns.forEach(pattern => {
        // 使用正则表达式匹配小标题，但不匹配第一个出现的（避免开头就换行）
        const regex = new RegExp(`(?<!^)${pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
        formattedDesc = formattedDesc.replace(regex, `<br>${pattern}`);
      });
      
      return formattedDesc;
    },
    
    // 防抖函数
    debounce(func, wait = 300) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    
    // 异步获取搜索建议
    querySearchAsync(queryString, cb) {
      if (!queryString || queryString.trim() === '') {
        cb([]);
        return;
      }
      
      // 调用获取关键词建议的API
      this.getKeywordsSuggestions(queryString.trim()).then(suggestions => {
        // 确保返回的是数组，并限制最多显示10条建议
        const validSuggestions = Array.isArray(suggestions) ? suggestions.slice(0, 10) : [];
        cb(validSuggestions);
      }).catch(error => {
        cb([]);
      });
    },
    
    // 获取关键词建议
    async getKeywordsSuggestions(keyword) {
      try {
        const result = await request('/api/Keywords/getKeywordsListByName', {
          keywords: keyword
        });
        
        // 验证返回数据结构
        if (!result) {
          return [];
        }
        
        let suggestionsList = [];
        
        // 检查返回数据的类型
        if (Array.isArray(result)) {
          suggestionsList = result;
        } else if (result.result && Array.isArray(result.result)) {
          if (result.code !== 200) {
            return [];
          }
          suggestionsList = result.result;
        } else if (result.data && Array.isArray(result.data)) {
          suggestionsList = result.data;
        } else {
          return [];
        }
        
        if (!Array.isArray(suggestionsList)) {
          return [];
        }
        
        // 将结果转换为autocomplete组件需要的格式
        const formattedSuggestions = suggestionsList.map(item => ({
          value: typeof item === 'string' ? item : String(item)
        }));
        
        return formattedSuggestions;
      } catch (error) {
        return [];
      }
    },
    
    // 处理用户选择建议项
    handleSelect(item) {
      this.inputValue = item.value;
      // 选择后立即执行搜索
      this.handlePressEnter();
    },
    
    // 处理输入变化
    handleInput(value) {
      // 这里可以添加额外的输入处理逻辑
      // AutoComplete组件已经内置了防抖功能
    },
    
    // 获取标签建议
    async queryTagSuggestions(queryString, cb) {
      if (!queryString || queryString.trim() === '') {
        cb([]);
        return;
      }
      
      try {
        const result = await request('/api/Keywords/getTagList');
        
        if (!result || !Array.isArray(result)) {
          cb([]);
          return;
        }
        
        // 过滤匹配的标签
        const keyword = queryString.trim().toLowerCase();
        const filteredTags = result
          .filter(item => item.tag && item.tag.toLowerCase().includes(keyword))
          .map(item => ({
            value: item.tag,
            mk_tag_id: item.mk_tag_id
          }))
          .slice(0, 10); // 最多显示10条
        
        cb(filteredTags);
      } catch (error) {
        cb([]);
      }
    },
    
    // 处理标签选择
    async handleTagSelect(item) {
      // 用户从下拉列表选择标签
      await this.addTag(item.value);
    },
    
    // 处理回车添加标签
    async handleAddTagEnter() {
      // 用户直接按回车，无论有没有搜索结果都添加
      if (this.tagValue && this.tagValue.trim()) {
        await this.addTag(this.tagValue);
      }
    },
    
    // 高亮匹配的文本
    highlightMatch(text, searchText) {
      if (!searchText || !searchText.trim()) {
        return text;
      }
      
      try {
        const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(regex, '<span class="highlight-match">$1</span>');
      } catch (error) {
        return text;
      }
    },
    
    async addTag(tagValue) {
      if (this.isSubmitting) {
        this.$message.warning('请勿重复提交');
        return;
      }

      if (!tagValue || tagValue.trim() === '') {
        this.$message.warning('标签名称不能为空');
        return;
      }

      try {
        this.isSubmitting = true;
        
        // 根据 member_keywords_id 的值决定使用哪个 keywords_id 和 source
        let keywordsId;
        let source;
        
        if (this.memberKeywordsId && this.memberKeywordsId !== 0) {
          // 如果 member_keywords_id 有值，使用 member_keywords_id，source 为 2
          keywordsId = this.memberKeywordsId;
          source = 2;
        } else {
          // 如果 member_keywords_id 为 0，使用 keywords_id，source 为 1
          keywordsId = this.currentKeywordsId;
          source = 1;
        }
        
        // 验证 keywords_id 是否有效
        if (!keywordsId || keywordsId === 0) {
          this.$message.error('词条ID无效，请先搜索一个关键词');
          return;
        }
        
        const result = await request('/api/Keywords/addTagByKeywordsId', {
          keywords_id: keywordsId,
          source: source,
          tag_name: tagValue
        });

        this.tagValue = '';
        this.$message.success('添加标签成功');
        
        // 重新获取数据并刷新页面，失败时不影响成功提示
        try {
          this.loading = true
          const allList = await this.getTagList()
          await this.getDetail()
          await this.beforeRenderWithErrorHandling(allList)
        } catch (refreshError) {
          // 刷新数据失败不影响成功提示，因为标签已经添加成功
          console.warn('刷新数据失败:', refreshError);
          this.loading = false;
        }
      } catch (error) {
        this.$message.error('添加标签失败，请重试');
      } finally {
        this.isSubmitting = false;
      }
    },
    async getDetail() {
      const result = await request('api/Keywords/getKeywordsInfoByName', {
        keywords: this.inputValue
      })
      this.currentKeywordsId = result.keywords_id
      this.memberKeywordsId = result.member_keywords_id
      this.label = this.inputValue
      this.tagListWithIds = result.tag_list || []
      this.tagList = result.tag_list ? result.tag_list.map(item => item.tags.tag) : []
      this.checkList = [...this.tagList]
      // 处理描述文本，在小标题前添加换行
      this.desc = this.formatDescription(result.description)

    },
    getTagList() {
      return new Promise((resolve, reject) => {
        request('/api/Keywords/getTagList').then((result) => {
          // 使用 Map 进行去重
          const uniqueMap = new Map();
          result.forEach(item => {
            if (!uniqueMap.has(item.mk_tag_id)) {
              uniqueMap.set(item.mk_tag_id, {
                id: item.mk_tag_id,
                name: item.tag
              });
            }
          });
          const allList = Array.from(uniqueMap.values());
          resolve(allList)
        }).catch((err) => {
          reject(err)
        })
      })
    },
    getData () {
      return new Promise((resolve, reject) => {
        const useKeywordsId = this.currentKeywordsId === 0 ? this.memberKeywordsId : this.currentKeywordsId
        const sourceValue = this.currentKeywordsId === 0 ? 2 : 1
        request('/api/Keywords/getCorrelationKeywordsList', {
          keywords_id: useKeywordsId,
          source: sourceValue
        }).then((result) => {
          // 保存keywords_info（包含书籍信息）
          if (result && result.keywords_info) {
            this.keywordsInfo = result.keywords_info;
          } else {
            this.keywordsInfo = null;
          }
          
          // 提取 correlation_keywords_list 中的 keywordsinfo.keywords
          if (result && result.correlation_keywords_list && Array.isArray(result.correlation_keywords_list)) {
            this.correlationKeywordsList = result.correlation_keywords_list
              .filter(item => item.keywordsinfo && item.keywordsinfo.keywords)
              .map(item => item.keywordsinfo.keywords);
          } else {
            this.correlationKeywordsList = [];
          }
          
          resolve(result)
        }).catch((err) => {
          this.correlationKeywordsList = [];
          this.keywordsInfo = null;
          reject(err)
        })
      })
    },
    initGraph(data) {
        graph && graph.destroy()
        register(ExtensionCategory.LAYOUT, 'bi', BiLayout);
        let plugins = getPlugins(data, this.graphData);
        if(plugins?.length === 1 && plugins?.[0]?.members?.length === 0) {
          plugins = []
        }
        
        const graphConfig = {
          container: 'container',
          data: this.graphData,
          autoFit: 'center',
          node: {
            style: {
              labelFill: '#000',
              labelPlacement: 'top',
              labelText: (d) => {
                const show = Object.keys(d.data).length === 0 && d.data.constructor === Object
                if(show) {
                  return ''
                } else {
                  return d.label
                }
              },
              fill: (d) => {
                // 当前词条显示彩色，其他显示灰色
                if (d.isSearchKeyword || d.isMain) {
                  return '#1783FF'
                } else {
                  return '#999999'
                }
              },
              stroke: (d) => {
                if (d.isSearchKeyword || d.isMain) {
                  return '#1783FF'
                } else {
                  return '#999999'
                }
              },
              cursor: (d) => {
                // 只有非搜索关键词的节点才显示手指指针
                if (!d.isSearchKeyword && !d.isMain) {
                  return 'pointer'
                } else {
                  return 'default'
                }
              }
            },
          },
          edge: {
            style: {
              stroke: 'transparent',
              lineWidth: 0,
              opacity: 0,
            }
          },
          zoom: 1,
          behaviors: ['drag-element', 'drag-canvas', 'zoom-canvas'],
          layout: {
            type: 'bi',
            sep: 300,
            nodeSep: 20,
            nodeSize: 32,
            preLayout: true,
          },
          plugins,
        };
        
        graph = new Graph(graphConfig);
        graph.render();
        
        // 添加节点点击事件监听
        graph.on('node:click', (evt) => {
          // G6 v5 的事件结构：evt.target 包含节点信息
          const target = evt.target;
          const nodeId = target?.id || evt.itemId;
          
          if (nodeId) {
            // 获取节点数据
            const nodeData = graph.getNodeData(nodeId);
            
            if (!nodeData) {
              return;
            }
            
            // 只处理非搜索关键词的节点（灰色的离散节点）
            const isSearchKeyword = nodeData.isSearchKeyword || nodeData.isMain;
            const isCurrentKeyword = nodeData.label === this.inputValue || nodeData.label === this.label;
            
            if (!isSearchKeyword && !isCurrentKeyword && nodeData.label) {
              this.handleNodeClick(nodeData.label);
            }
          }
        });
        
        // 图形渲染完成
    },
    beforeRender (allList) {
      this.getData().then(data => {
        this.loading = false
        
        // 构建完整的数据结构
        const processedData = {
          ...data,
          keywords_info: {
            keywords: this.inputValue,
            label_list: this.tagListWithIds.map(item => ({
              id: item.tags.mk_tag_id,
              name: item.tags.tag
            })),
            friend_list: data?.correlation_keywords_list ? toFriendList(data.correlation_keywords_list) : []
          },
          label_all_list: allList,
          inputValue: this.inputValue
        };
        
        // 检查是否有关联数据
        const hasCorrelationData = data && data.correlation_keywords_list && data.correlation_keywords_list.length > 0;
        
        if (!hasCorrelationData) {
          const result = getBiData(processedData);
          
          this.graphData = {
            nodes: result.intersectionNodes || [],
            edges: result.edges || []
          };
          

          this.initGraph(processedData);
          
          // 提取相关词条
          this.relatedKeywords = this.extractRelatedKeywords(processedData);
          return;
        }

        // 有关联数据的原有处理逻辑
        const tags = data.correlation_keywords_list[0]?.tag_ids || ''
        let friendList = toFriendList(data.correlation_keywords_list)
        processedData.keywords_info.label = Array.from(new Set(tags.split(','))).join(',')
        processedData.keywords_info.friend_list = friendList
        const labelList = getLabelList(data.keywords_tag_list?.flat() || [])
        processedData.keywords_info.label_list = labelList

        const result = getBiData(processedData || {})
        if (result) {
          const { intersectionNodes, uniqueNodeData } = result
          this.graphData = {
            nodes: [...intersectionNodes, ...uniqueNodeData],
            edges: result.edges || []
          }
          this.initGraph(processedData)
          
          // 提取相关词条
          this.relatedKeywords = this.extractRelatedKeywords(processedData);
        }
      }).catch(error => {
        this.loading = false
        this.$message.error('获取数据失败，请重试')
      })
    },
    // 带错误处理的 beforeRender，返回 Promise，用于在添加标签后刷新数据
    async beforeRenderWithErrorHandling(allList) {
      return new Promise((resolve, reject) => {
        this.getData().then(data => {
          this.loading = false
          
          // 构建完整的数据结构
          const processedData = {
            ...data,
            keywords_info: {
              keywords: this.inputValue,
              label_list: this.tagListWithIds.map(item => ({
                id: item.tags.mk_tag_id,
                name: item.tags.tag
              })),
              friend_list: data?.correlation_keywords_list ? toFriendList(data.correlation_keywords_list) : []
            },
            label_all_list: allList,
            inputValue: this.inputValue
          };
          
          // 检查是否有关联数据
          const hasCorrelationData = data && data.correlation_keywords_list && data.correlation_keywords_list.length > 0;
          
          if (!hasCorrelationData) {
            const result = getBiData(processedData);
            
            this.graphData = {
              nodes: result.intersectionNodes || [],
              edges: result.edges || []
            };
            
            this.initGraph(processedData);
            
            // 提取相关词条
            this.relatedKeywords = this.extractRelatedKeywords(processedData);
            resolve();
            return;
          }

          // 有关联数据的原有处理逻辑
          const tags = data.correlation_keywords_list[0]?.tag_ids || ''
          let friendList = toFriendList(data.correlation_keywords_list)
          processedData.keywords_info.label = Array.from(new Set(tags.split(','))).join(',')
          processedData.keywords_info.friend_list = friendList
          const labelList = getLabelList(data.keywords_tag_list?.flat() || [])
          processedData.keywords_info.label_list = labelList

          const result = getBiData(processedData || {})
          if (result) {
            const { intersectionNodes, uniqueNodeData } = result
            this.graphData = {
              nodes: [...intersectionNodes, ...uniqueNodeData],
              edges: result.edges || []
            }
            this.initGraph(processedData)
            
            // 提取相关词条
            this.relatedKeywords = this.extractRelatedKeywords(processedData);
          }
          resolve();
        }).catch(error => {
          this.loading = false
          // 不显示错误消息，让调用方决定如何处理
          reject(error)
        })
      })
    },
    async handlePressEnter() {
      this.loading = true
      const allList = await this.getTagList()
      await this.getDetail()
      this.beforeRender(allList)
    },
    
    // 处理词条点击事件
    handleEntryClick(entry) {
      // 将词条名称设置到搜索框并搜索
      this.inputValue = entry.name
      this.handlePressEnter()
    },
    async handleCheckboxChange(value) {
      // 找出被取消勾选的标签
      const uncheckedTags = this.tagList.filter(tag => !value.includes(tag));
      
      if (uncheckedTags.length > 0) {
        // 处理每个被取消勾选的标签
        for (const uncheckedTag of uncheckedTags) {
          await this.unbindTag(uncheckedTag);
        }
        // 重新获取数据并绘制图表
        await this.reloadData();
      }
      
      this.checkList = value;
    },
    async unbindTag(tagName) {
      try {
        // 从完整标签信息中找到对应的keywords_tag_id
        const tagInfo = this.tagListWithIds.find(item => item.tags.tag === tagName);
        if (!tagInfo) {
          return;
        }

        await request('/api/Keywords/unbindTagKeywords', {
          keywords_tag_id: tagInfo.keywords_tag_id
        });
        
        this.$message.success(`已解绑标签: ${tagName}`);
      } catch (error) {
        this.$message.error(`解绑标签失败: ${tagName}`);
      }
    },
    async reloadData() {
      try {
        this.loading = true;
        const allList = await this.getTagList();
        await this.getDetail();
        await this.beforeRenderWithErrorHandling(allList);
      } catch (error) {
        // 刷新数据失败不影响成功提示，因为解绑操作已经成功
        console.warn('刷新数据失败:', error);
        this.loading = false;
      }
    },
    async getAiTags() {
      if (this.isGettingAiTags) {
        this.$message.warning('请稍后再试');
        return;
      }

      try {
        this.isGettingAiTags = true;
        
        // AI智能体配置 - 使用代理路径避免CORS问题
        const COZE_TOKEN_URL = '/ai/coze/get-online';  // 使用代理路径避免CORS问题
        const COZE_BOT_URL = 'https://api.coze.cn/v3/chat';
        const BOT_ID = '7569521322913579048';
        
        // 获取token
        const tokenResponse = await fetch(COZE_TOKEN_URL, {
          method: 'GET',
          headers: {
            'Authorization': localStorage.getItem('authorization')
          }
        });
        
        if (!tokenResponse.ok) {
          throw new Error(`获取token失败: ${tokenResponse.status}`);
        }
        
        const tokenData = await tokenResponse.json();
        
        if (!tokenData || !tokenData.success || !tokenData.data || !tokenData.data.access_token) {
          throw new Error('获取token失败');
        }
        
        const accessToken = tokenData.data.access_token;
        this.accessToken = accessToken; // 保存token用于轮询
        
        // 构建智能体请求数据
        // 只提供关键词给智能体
        const inputText = this.inputValue;
        
        // 先创建conversation
        const createConversationData = {
          bot_id: BOT_ID,
          user_id: "123456",
          stream: false,
          auto_save_history: true,
          additional_messages: [
            {
              role: "user",
              content: inputText,
              content_type: "text"
            }
          ]
        };
        
        // 创建conversation
        const response = await fetch(COZE_BOT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(createConversationData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const responseText = await response.text();
        
        let aiResponse;
        try {
          aiResponse = JSON.parse(responseText);
        } catch (e) {
          throw new Error('响应格式错误');
        }
        
        // 检查响应结构，创建conversation返回的是 { code: 0, data: { chat_id, conversation_id } }
        if (aiResponse && aiResponse.code === 0 && aiResponse.data) {
          try {
            // 需要轮询获取消息内容
            const chatId = aiResponse.data.id; // 注意：响应中的字段是 'id' 而不是 'chat_id'
            const conversationId = aiResponse.data.conversation_id;
            
            // 轮询获取消息
            const messages = await this.pollMessages(chatId, conversationId);
            if (messages && messages.length > 0) {
              const answerMessage = messages.find(msg => msg.type === 'answer');
              if (answerMessage && answerMessage.content) {
                // 将输出字符串按逗号分割，并过滤空字符串
                this.aiTags = answerMessage.content
                  .split(/[,，、]/)
                  .map(item => item.trim())
                  .filter(item => item.length > 0);
                
                this.selectedAiTags = [];
                this.$message.success('AI推荐标签获取成功');
              } else {
                throw new Error('未找到AI回答内容');
              }
            } else {
              throw new Error('未获取到消息内容');
            }
          } catch (e) {
            throw new Error('解析AI响应失败');
          }
        } else {
          throw new Error('AI智能体调用失败');
        }
      } catch (error) {
        // 检查是否是CORS错误
        if (error.message && error.message.includes('CORS')) {
          this.$message.error('网络配置问题，请联系管理员解决CORS设置');
        } else if (error.message && error.message.includes('Network Error')) {
          this.$message.error('网络连接失败，请检查网络或稍后重试');
        } else {
          this.$message.error('获取AI推荐标签失败，请重试');
        }
        
        this.aiTags = [];
        this.selectedAiTags = [];
      } finally {
        this.isGettingAiTags = false;
      }
    },
    async addSelectedAiTags() {
      if (!this.selectedAiTags.length) {
        this.$message.warning('请选择要添加的标签');
        return;
      }
      
      try {
        // 批量添加选中的AI标签到后端
        for (const tagName of this.selectedAiTags) {
          await this.addTag(tagName);
        }
        
        // 清空AI标签选择
        this.selectedAiTags = [];
        this.aiTags = [];
        
        // 重新获取数据并刷新页面
        try {
          this.loading = true;
          const allList = await this.getTagList();
          await this.getDetail();
          await this.beforeRenderWithErrorHandling(allList);
        } catch (refreshError) {
          // 刷新数据失败不影响成功提示，因为标签已经添加成功
          console.warn('刷新数据失败:', refreshError);
        }
        
        this.$message.success('AI推荐标签添加成功');
      } catch (error) {
        this.$message.error('添加AI标签失败，请重试');
      }
    },
    clearAiTags() {
      this.selectedAiTags = [];
      this.aiTags = [];
      this.$message.info('已清除AI推荐');
    },
    
    // 轮询获取消息内容
    async pollMessages(chatId, conversationId) {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 20; // 最多尝试20次
        
        const poll = async () => {
          try {
            attempts++;
            
            const response = await fetch(`https://api.coze.cn/v3/chat/message/list?chat_id=${chatId}&conversation_id=${conversationId}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.code === 0) {
              const messages = data.data || [];
              if (messages.length > 0) {
                resolve(messages);
                return;
              }
            }
            
            if (attempts >= maxAttempts) {
              reject(new Error('轮询超时，未获取到消息'));
              return;
            }
            
            // 3秒后重试
            setTimeout(poll, 3000);
          } catch (error) {
            if (attempts >= maxAttempts) {
              reject(error);
              return;
            }
            setTimeout(poll, 3000);
          }
        };
        
        poll();
      });
    },
    
    // 切换搜索关键词的方法
    async switchSearchKeyword(newKeyword) {
      try {
        // 更新输入框的值
        this.inputValue = newKeyword;
        
        // 通知导航栏更新搜索框内容
        this.$bus.$emit('updateSearchInput', newKeyword);
        
        // 显示加载状态
        this.loading = true;
        
        // 清空AI推荐标签
        this.aiTags = [];
        this.selectedAiTags = [];
        
        // 重新获取数据
        const allList = await this.getTagList();
        await this.getDetail();
        this.beforeRender(allList);
        
        this.$message.success(`已切换到搜索词条: ${newKeyword}`);
      } catch (error) {
        this.$message.error('切换搜索关键词失败，请重试');
        this.loading = false;
      }
    },
    
    // 处理相关词条点击
    async handleRelatedKeywordClick(keyword) {
      try {
        await this.switchSearchKeyword(keyword);
      } catch (error) {
        this.$message.error('切换失败，请重试');
      }
    },
    
    // 处理离散节点点击，将标签添加到相关标签列表
    async handleNodeClick(nodeName) {
      try {
        
        // 检查是否已搜索关键词
        if (!this.inputValue || !this.inputValue.trim()) {
          this.$message.warning('请先搜索一个关键词');
          return;
        }
        
        // 检查是否有 keywords_id
        if (!this.currentKeywordsId && !this.memberKeywordsId) {
          this.$message.warning('请先搜索一个关键词以获取词条信息');
          return;
        }
        
        // 检查标签是否已存在
        if (this.tagList.includes(nodeName)) {
          this.$message.info(`标签 "${nodeName}" 已存在`);
          return;
        }
        
        // 添加标签
        await this.addTag(nodeName);
        
        this.$message.success(`已添加标签: ${nodeName}`);
      } catch (error) {
        this.$message.error('添加标签失败，请重试');
      }
    },
    
    // 提取相关词条
    extractRelatedKeywords(data) {
      const relatedKeywords = [];
      
      try {
        // 从气泡集合插件中提取灰色词条
        const plugins = getPlugins(data, this.graphData);
        
        if (plugins && plugins.length > 0) {
          plugins.forEach(plugin => {
            // 只获取灰色的气泡集合（非搜索关键词）
            if (plugin.fill === '#999999' && plugin.labelText && plugin.labelText.trim()) {
              relatedKeywords.push(plugin.labelText.trim());
            }
          });
        }
        
        return relatedKeywords;
      } catch (error) {
        return [];
      }
    },
    
    // 处理加号按钮点击
    handleAddKeywordClick() {
      // 如果相关词条列表为空，不执行操作
      if (this.correlationKeywordsList.length === 0) {
        return;
      }
      // 打开弹窗
      this.showKeywordDialog = true;
    },
    
    // 选择关键词
    selectKeyword(keyword) {
      this.selectedKeyword = keyword;
      // 清除之前选择的箭头
      this.selectedArrowIndex = null;
    },
    
    // 选择箭头
    selectArrow(index) {
      this.selectedArrowIndex = index;
    },
    
    // 重置选择
    resetSelection() {
      this.selectedKeyword = '';
      this.selectedArrowIndex = null;
    },
    
    // 保存关键词关系
    async saveKeywordRelation() {
      if (!this.selectedKeyword || !this.selectedArrowIndex) {
        this.$message.warning('请选择关键词和关系类型');
        return;
      }
      
      try {
        this.isSaving = true;
        
        // 调用接口保存，直接绑定关键词和关系类型
        await request('/api/Keywords/addMixKeywordsLines', {
          keywords_front: this.inputValue, // 当前搜索词作为前置关键词
          keywords_behind: this.selectedKeyword, // 选中的词作为后置关键词
          lines_code: this.selectedArrowIndex // 选中的箭头索引
        });
        
        this.$message.success('保存成功');
        
        // 关闭弹窗并重置
        this.showKeywordDialog = false;
        this.resetSelection();
        
      } catch (error) {
        // 检查是否是连线已存在的错误
        if (error && error.code === 100 && error.message === '连线已存在') {
          this.$message.warning('连线已存在');
        } else {
          this.$message.error('保存失败，请重试');
        }
      } finally {
        this.isSaving = false;
      }
    },
    
    // 处理书籍链接点击
    handleBookLinkClick() {
      if (!this.inputValue || !this.inputValue.trim()) {
        this.$message.warning('词条名称为空');
        return;
      }
      
      const bookId = this.bookInfo?.book_id;
      
      if (!bookId) {
        this.$message.warning('未找到书籍信息');
        return;
      }
      
      // 跳转到星云图页面并执行搜索，同时传递书籍ID
      this.$router.push({
        path: '/index',
        query: {
          keyword: this.inputValue,
          bookId: bookId
        }
      });
    },
  },
  async mounted() {
    // 从store获取搜索关键词
    const keyword = this.$store.state.bubbleSearchKeyword
    if (keyword) {
      this.inputValue = keyword
      // 清除store中的关键词，避免重复使用
      this.$store.commit('setBubbleSearchKeyword', '')
    }
    
    if (this.inputValue) {
      const allList = await this.getTagList()
      await this.getDetail()
      this.beforeRender(allList)
    } else {
      this.loading = false
    }
    
    // 监听来自导航栏的搜索事件
    this.$bus.$on('bubbleSearch', (keyword) => {
      this.inputValue = keyword
      this.handlePressEnter()
    })
  },
  
  beforeDestroy() {
    // 清除事件监听
    this.$bus.$off('bubbleSearch')
  }
}

</script>
<style lang="scss" scoped>
/* 白板词条列表悬浮框样式 */
.entry-list-panel {
  position: fixed;
  top: 80px;
  left: 20px;
  width: 180px;
  max-height: 400px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.entry-list-header {
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  
  i {
    cursor: pointer;
    font-size: 14px;
    color: #999;
    transition: color 0.2s ease;
    
    &:hover {
      color: #f56c6c;
    }
  }
}

.entry-list-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #dcdcdc;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #c0c0c0;
  }
}

.entry-item {
  padding: 8px 10px;
  margin-bottom: 4px;
  background: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &:hover {
    background: #667eea;
    color: white;
    transform: translateX(2px);
  }
  
  &:active {
    transform: translateX(1px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

/* 动画效果 */
.entry-list-fade-enter-active,
.entry-list-fade-leave-active {
  transition: all 0.3s ease;
}

.entry-list-fade-enter,
.entry-list-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* 相关词条容器样式 */
.related-keywords-container {
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  padding: 12px;
  margin-top: 10px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.related-keywords-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.related-keywords-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.add-keyword-btn {
  font-size: 20px;
  color: #667eea;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #5a67d8;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &.disabled {
    color: #d0d0d0;
    cursor: not-allowed;
    
    &:hover {
      color: #d0d0d0;
      transform: scale(1);
    }
  }
}

.related-keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 120px;
  overflow-y: auto;
  align-content: flex-start;
  
  .empty-placeholder {
    width: 100%;
    text-align: center;
    color: #999;
    font-size: 13px;
    padding: 20px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.related-keyword-item {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
}

/* 滚动条样式 */
.related-keywords-list::-webkit-scrollbar {
  width: 4px;
}

.related-keywords-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.related-keywords-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.related-keywords-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}


/* 自定义 autocomplete 下拉面板样式 */
::v-deep .el-autocomplete-suggestion {
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.12);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: #ffffff;
  backdrop-filter: blur(10px);
  overflow: hidden;
  margin-top: 8px;
}

::v-deep .el-autocomplete-suggestion__list {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px 0;
  
  /* 自定义滚动条 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f3f4;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 3px;
    
    &:hover {
      background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    }
  }
}

::v-deep .el-autocomplete-suggestion__wrap {
  max-height: 280px;
}

::v-deep .el-autocomplete-suggestion li {
  border-bottom: none;
  padding: 0;
  line-height: normal;
  
  &:hover {
    background-color: transparent !important;
  }
  
  &.highlighted {
    background-color: transparent !important;
  }
}

/* 添加加载状态指示器 */
::v-deep .el-autocomplete-suggestion.is-loading {
  .el-autocomplete-suggestion__list {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60px;
    
    &::before {
      content: "搜索中...";
      color: #909399;
      font-size: 14px;
    }
  }
}
.g6-demo {
  width: 100%;
  height: 100%;
  position: relative;
}
#container {
  width: 100%;
  height: 100%;
}

/* 流程进度触发图标样式 */
.flow-trigger-icon {
  position: fixed;
  right: 30px;
  bottom: 30px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1500;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  i {
    font-size: 26px;
    color: white;
    z-index: 2;
  }

  .icon-pulse {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    animation: pulse-ring 2s cubic-bezier(0.25, 0.8, 0.25, 1) infinite;
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 30px rgba(102, 126, 234, 0.6);
    
    i {
      animation: icon-rotate 0.6s ease;
    }
  }

  &:active {
    transform: scale(0.95);
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.4;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes icon-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.right-con{
  position: absolute;
  right: 40px;
  top: 80px;
  max-height: calc(100vh - 100px);
  border-radius: 10px;
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 0;
  background-color: transparent;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 20px;
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
  
  .top{
    max-height: 200px;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    flex-shrink: 0;
    .title{
      font-size: 16px;
      font-weight: 600;
      flex-shrink: 0;
    }
    .desc{
      font-size: 14px;
      color: #666;
      margin-top: 8px;
      line-height: 1.5;
      flex: 1;
      overflow-y: auto;
      word-wrap: break-word;
      word-break: break-all;
    }
    
    .book-link-container {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #f0f0f0;
      flex-shrink: 0;
      
      .book-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: #667eea;
        font-size: 13px;
        text-decoration: none;
        transition: all 0.2s ease;
        padding: 4px 8px;
        border-radius: 4px;
        
        &:hover {
          background: #f0f4ff;
          color: #5a67d8;
          
          i {
            transform: translateX(2px);
          }
        }
        
        i {
          transition: transform 0.2s ease;
          
          &.el-icon-reading {
            font-size: 14px;
          }
          
          &.el-icon-right {
            font-size: 12px;
          }
        }
        
        span {
          max-width: 280px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
  .tag-list{
    margin-top: 10px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    padding: 16px;
    display: flex;
    max-height: 280px;
    flex-direction: column;
    gap: 10px;
    background-color: #fff;
    border-radius: 10px;
    flex-shrink: 0;
    
    .tag-list-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
      
      .title {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
        white-space: nowrap;
      }
      
      .add-tag-input {
        flex: 1;
        max-width: 220px;
        
        ::v-deep .el-input__inner {
          border-radius: 6px;
          border: 1px solid #e0e0e0;
          transition: all 0.3s ease;
          
          &:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
          }
          
          &::placeholder {
            font-size: 12px;
          }
        }
        
        ::v-deep .el-input__suffix {
          right: 8px;
        }
        
        // 标签建议下拉样式
        ::v-deep .el-autocomplete-suggestion {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          
          li {
            padding: 8px 12px;
            
            &:hover {
              background-color: #f5f7fa;
            }
            
            &.highlighted {
              background-color: #f0f4ff;
            }
          }
        }
      }
    }
    
    // 标签建议项样式
    .tag-suggestion-item {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .el-icon-price-tag {
        color: #667eea;
        font-size: 14px;
      }
      
      .tag-suggestion-text {
        font-size: 13px;
        color: #303133;
      }
    }
    
    .content-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    
    .related-tags-section {
      flex: 1;
      min-height: 0;
      
      .scrollable-content {
        max-height: calc(50vh - 120px); /* 默认占满整个可用空间 */
        overflow-y: auto;
        padding-right: 5px;
        transition: max-height 0.3s ease;
        
        &::-webkit-scrollbar {
          width: 4px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 2px;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        .el-checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      }
    }
    
    /* 当有AI标签时，调整相关词条区域高度 */
    .content-wrapper.has-ai-tags .related-tags-section .scrollable-content {
      max-height: calc(23vh - 60px); /* 有AI标签时分配较小空间 */
    }
    
    .ai-separator-section {
      margin-top: 15px;
      flex: 1; /* AI标签区域也占一半 */
      
      .separator {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 10px 0;
        
        .separator-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #ddd, transparent);
        }
        
        .separator-text {
          font-size: 13px;
          font-weight: 600;
          color: #666;
          background: #f8f9fa;
          padding: 4px 12px;
          border-radius: 12px;
          border: 1px solid #e9ecef;
        }
      }
      
      .ai-tags-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0; /* 确保flex子元素可以正确收缩 */
        
        .scrollable-content {
          max-height: calc(20vh - 100px); /* AI标签区域的滚动高度，为按钮预留空间 */
          overflow-y: auto;
          padding-right: 5px;
          margin-bottom: 10px;
          flex-shrink: 1; /* 允许收缩，但不强制占用所有空间 */
          min-height: 0; /* 允许收缩到0 */
          
          &::-webkit-scrollbar {
            width: 4px;
          }
          
          &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
          }
          
          &::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 2px;
          }
          
          &::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          .el-checkbox-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          
          .el-checkbox {
            background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
            border: 1px solid #e1e8ff;
            border-radius: 6px;
            padding: 8px 12px;
            margin: 0;
            transition: all 0.2s ease;
            
            &:hover {
              background: linear-gradient(135deg, #e8f2ff 0%, #dbeafe 100%);
              border-color: #667eea;
            }
            
            &.is-checked {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              border-color: #667eea;
              color: white;
              
              .el-checkbox__label {
                color: white;
              }
            }
          }
        }
        
        .ai-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0; /* 防止按钮区域被压缩 */
          padding-top: 8px; /* 添加顶部间距 */
          margin-top: auto; /* 推到底部 */
          position: relative; /* 确保按钮在最上层 */
          z-index: 10; /* 确保按钮不被遮挡 */
          
          .ai-add-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
            margin: 0;
            
            &:hover {
              background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
              border-color: #5a67d8;
            }
          }
          
          .ai-clear-btn {
            color: #666;
            margin: 0;
            
            &:hover {
              color: #ff6b6b;
            }
          }
        }
      }
    }
    
    .el-checkbox-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
  
  .action-section {
    margin-top: 15px;
    padding: 0 16px;
    
    .ai-btn {
      width: 100%;
      cursor: pointer;
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
      border-color: #ff6b6b;
      height: 40px;
      font-weight: 600;
      
      &:hover {
        background: linear-gradient(135deg, #ff5252 0%, #d84439 100%);
        border-color: #ff5252;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.related-tags-section {
  flex: 1;
  overflow-y: auto;
}

.scrollable-content {
  max-height: 100px;
  overflow-y: auto;
}

.ai-separator-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.separator {
  display: flex;
  align-items: center;
  gap: 10px;
}

.separator-line {
  flex: 1;
  height: 1px;
  background-color: #ccc;
}

.separator-text {
  font-size: 14px;
  font-weight: 600;
}

.ai-tags-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ai-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-add-btn {
  align-self: flex-start;
  margin-top: 8px;
}

.ai-clear-btn {
  align-self: flex-start;
  margin-top: 8px;
}

/* 相关词条选择弹窗样式 */
::v-deep .keyword-selection-dialog {
  border-radius: 8px;
  
  .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    
    .el-dialog__title {
      color: #333;
      font-weight: 600;
      font-size: 15px;
    }
    
    .el-dialog__close {
      color: #909399;
      font-size: 18px;
      
      &:hover {
        color: #606266;
      }
    }
  }
  
  .el-dialog__body {
    padding: 16px 20px;
  }
}

.keyword-selection-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.selection-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 4px;
}

.keywords-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 2px;
    
    &:hover {
      background: #b0b0b0;
    }
  }
}

.keyword-selection-item {
  background: #f5f7fa;
  color: #333;
  padding: 6px 14px;
  border-radius: 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e4e7ed;
  
  &:hover {
    background: #ecf5ff;
    color: #409eff;
    border-color: #c6e2ff;
  }
  
  &.selected {
    background: #409eff;
    color: white;
    border-color: #409eff;
  }
}

.arrows-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  justify-items: center;
}

.arrow-item {
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  
  img {
    width: 50px;
    height: auto;
    display: block;
    opacity: 0.6;
    transition: all 0.2s ease;
  }
  
  &:hover img {
    opacity: 1;
    transform: scale(1.1);
  }
  
  &.selected img {
    opacity: 1;
    transform: scale(1.15);
  }
}

.action-section {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.empty-keywords {
  width: 100%;
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 30px 0;
}
</style>


